/**
 * One run of the connection check, kept apart from React so its lifetime is
 * explicit: a run starts when the transport, the thresholds or the attempt
 * change, and for nothing else.
 *
 * In particular not for the callbacks. A page that re-renders — the study
 * page does every second while an idle deadline ticks — hands the gate new
 * `onPass`/`onFail` functions each time. Restarting on those restarted the
 * twelve pings from zero every second, so the check never finished. The
 * callbacks are held as "the latest", and called when the run ends.
 */

import type { LatencyConfig } from '@overcooked/shared'
import type { LatencyResult, LatencyVerdict, PingTransport } from '../voice/latency'
import { classify, sampleLatency } from '../voice/latency'

export interface LatencyGateHandlers {
  onPass(result: LatencyResult): void
  onFail(verdict: LatencyVerdict, result: LatencyResult): void
}

export interface LatencyGateState {
  /** 0–1. */
  progress: number
  outcome: { verdict: LatencyVerdict; result: LatencyResult } | null
}

export interface LatencyGateRunOptions {
  /** Gap between pings; the sampler's own default otherwise. */
  intervalMs?: number
  /** Called each time a run starts — for tests. */
  onStart?(): void
}

export class LatencyGateRun {
  private key: { transport: PingTransport; config: LatencyConfig; attempt: number } | null = null
  private handlers: LatencyGateHandlers | null = null
  private generation = 0

  constructor(
    private readonly onState: (state: LatencyGateState) => void,
    private readonly options: LatencyGateRunOptions = {},
  ) {}

  /** Every render: remember the newest callbacks; start a run only if what it measures changed. */
  update(transport: PingTransport, config: LatencyConfig, attempt: number, handlers: LatencyGateHandlers): void {
    this.handlers = handlers
    const key = this.key
    if (key && key.transport === transport && key.config === config && key.attempt === attempt) return
    this.key = { transport, config, attempt }
    this.start(transport, config)
  }

  /** Unmounted: whatever is in flight reports nothing, and the next `update` starts afresh. */
  dispose(): void {
    this.generation += 1
    this.key = null
  }

  private start(transport: PingTransport, config: LatencyConfig): void {
    const generation = ++this.generation
    const current = (): boolean => generation === this.generation
    this.options.onStart?.()
    this.onState({ progress: 0, outcome: null })
    void sampleLatency(transport, {
      count: config.gateSampleCount,
      timeoutMs: config.gateSampleTimeoutMs,
      ...(this.options.intervalMs !== undefined ? { intervalMs: this.options.intervalMs } : {}),
      onProgress: (done, total) => {
        if (current()) this.onState({ progress: done / total, outcome: null })
      },
    }).then((result) => {
      if (!current()) return
      const verdict = classify(result, config.gateMaxMedianRttMs)
      if (verdict === 'pass') {
        this.handlers?.onPass(result)
      } else {
        this.onState({ progress: 1, outcome: { verdict, result } })
        this.handlers?.onFail(verdict, result)
      }
    })
  }
}
